import { trimLeadingWhitespace } from "@/utils/utils";

import { fhirR4 } from "@smile-cdr/fhirts";
import { isEqual } from "lodash";
// * Enable OneDashboard *: OneDashboard uses a newer version of uuid.
import { v4 as uuidv4 } from "uuid";

import { AlertSeverityFilterEnum, AlertStateFilterEnum, ConditionCode } from "../types/types";
import {
  ABNORMAL_READING_CODE,
  ADHERENCE_ALERT_CODE,
  CONDITION_CODING,
  VITALL_TASK_TYPE_SYSTEM,
} from "./fhir-constants";
import { JsonPatchResource } from "./fhir-json-patch-utils";
import { mapPriorityToSeverity } from "./fhir-task-filter-utils";
import { HCN_TYPE_CODE, MRN_TYPE_CODE } from "./naming-system";

// * Enable Legacy Patient *: Legacy patients use an older version of uuid.
//const { v4: uuidv4 } = require('uuid');

export const FHIR_MEDIA_TYPE_SYSTEM = "http://terminology.hl7.org/CodeSystem/media-type";
export const SNOMED_CODE_SYSTEM = "http://snomed.info/sct";

/**
 * @deprecated Use VITALL_TASK_TYPE_SYSTEM from fhir-constants instead.
 */
export const VITALL_TASK_SYSTEM = VITALL_TASK_TYPE_SYSTEM;

/**
 * Checks if a Task is a Medly CHF algorithm task by matching the abnormal-reading task type
 * and the CHF SNOMED condition code.
 */
export const isMedlyCHFAlgorithmTask = (task: fhirR4.Task): boolean => {
  const codings = task.code?.coding;
  if (!codings) {
    return false;
  }

  const hasAbnormalReading = codings.some(
    coding => coding.system === VITALL_TASK_TYPE_SYSTEM && coding.code === ABNORMAL_READING_CODE
  );
  const hasCHFCondition = codings.some(
    coding => coding.system === CONDITION_CODING.snomed.system && coding.code === ConditionCode.congestiveHeartFailure
  );

  return hasAbnormalReading && hasCHFCondition;
};

/**
 * Checks if a Task is an adherence alert task by matching the adherence task type
 * and the CHF SNOMED condition code.
 */
export const isAdherenceAlertTask = (task: fhirR4.Task): boolean => {
  const codings = task.code?.coding;
  if (!codings) {
    return false;
  }

  const hasAdherence = codings.some(
    coding => coding.system === VITALL_TASK_TYPE_SYSTEM && coding.code === ADHERENCE_ALERT_CODE
  );
  const hasCHFCondition = codings.some(
    coding => coding.system === CONDITION_CODING.snomed.system && coding.code === ConditionCode.congestiveHeartFailure
  );

  return hasAdherence && hasCHFCondition;
};

export const USER_INVITE_DATE_EXTENSION_URL = "http://vitall.com/fhir/ext/user-invite-date";

export enum HttpMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

// Enum for the different media types of a `fhirR4.Media`.
export enum FhirR4MediaTypeCode {
  IMAGE = "image",
  AUDIO = "audio",
  VIDEO = "video",
}

// Enum for the different statuses of a `fhirR4.Media`.
export enum FhirR4MediaStatus {
  // NOTE: We only include the `completed` status for a `fhirR4.Media` at the moment.
  COMPLETED = "completed",
}

/**
 * Extracts the resource ID from a FHIR reference string (e.g. "Patient/123" -> "123").
 * @param reference - The FHIR reference string.
 * @param resourceType - The resource type to extract from (e.g. "Patient", "Observation").
 * @returns The resource ID, or undefined if the reference doesn't match.
 */
export const extractFhirResourceId = (reference: string | undefined, resourceType: string): string | undefined => {
  if (!reference) {
    return undefined;
  }
  const prefix = `${resourceType}/`;
  if (!reference.startsWith(prefix)) {
    return undefined;
  }
  return reference.slice(prefix.length) || undefined;
};

/**
 * Overlay a form's identifier edits onto the English base's identifier array. Each edited identifier
 * is matched to a base entry by a shared type code; on a match the base entry's coded `type` (whose
 * display a localized GET translates) is kept and only the form-owned `value` and `extension` are
 * taken from the edit. An edited identifier with no base match is kept as is, since the form builds a
 * new one from an English template.
 *
 * @param baseIdentifiers - identifiers from the English base patient (untranslated).
 * @param editedIdentifiers - identifiers the form produced (may carry translated type displays).
 * @returns the identifier array to persist.
 */
const mergeIdentifiersOntoBase = (
  baseIdentifiers: fhirR4.Identifier[] | undefined,
  editedIdentifiers: fhirR4.Identifier[]
): fhirR4.Identifier[] => {
  const base = baseIdentifiers ?? [];
  const typeCodes = (id: fhirR4.Identifier): string[] =>
    (id.type?.coding ?? []).map(c => c.code).filter((c): c is string => !!c);

  return editedIdentifiers.map(edited => {
    const editedCodes = typeCodes(edited);
    const match = base.find(b => typeCodes(b).some(code => editedCodes.includes(code)));
    if (!match) {
      return edited;
    }
    // Keep the base's use/type/system; take only the fields the form edits.
    const merged: fhirR4.Identifier = { ...match, value: edited.value };
    if (edited.extension !== undefined) {
      merged.extension = edited.extension;
    } else {
      delete merged.extension;
    }
    return merged;
  });
};

/**
 * Overlay a form's Patient edits onto an untranslated (English) copy refetched from the server.
 * Every top-level field the user did not change keeps the English base's value, so coded displays
 * the form does not edit (maritalStatus, communication.language, identifier type) are written back
 * in English rather than the translation shown under a non-English Accept-Language. A field the user
 * changed takes the edited value; a field the edit removed is dropped. The identifier array is merged
 * per entry so changing one identifier's value does not drag back the translated type display of the
 * whole array.
 *
 * @param base - the Patient refetched without Accept-Language (the stored, untranslated resource).
 * @param original - the Patient the form was seeded from (may carry translated displays).
 * @param edited - the Patient the form produced.
 * @returns the Patient to persist: the English base with the user's actual changes applied.
 */
export const mergePatientEditsOntoBase = (
  base: fhirR4.Patient,
  original: fhirR4.Patient,
  edited: fhirR4.Patient
): fhirR4.Patient => {
  const result = { ...base } as Record<string, unknown>;
  const keys = new Set<string>([...Object.keys(original), ...Object.keys(edited)]);
  const originalRecord = original as unknown as Record<string, unknown>;
  const editedRecord = edited as unknown as Record<string, unknown>;
  for (const key of keys) {
    if (isEqual(originalRecord[key], editedRecord[key])) {
      continue;
    }
    if (!(key in editedRecord)) {
      delete result[key];
      continue;
    }
    if (key === "identifier") {
      result[key] = mergeIdentifiersOntoBase(base.identifier, edited.identifier ?? []);
      continue;
    }
    result[key] = editedRecord[key];
  }
  return result as unknown as fhirR4.Patient;
};

/**
 * Utility function to extract an identifier of a specific type from a list of FHIR Identifiers.
 * @param identifiers - The list of FHIR Identifiers.
 * @param typeCode - v2-0203 type code to match against `identifier.type.coding[].code`.
 * @returns The value and extension of the identifier.
 */
export const getIdentifier = (
  identifiers: fhirR4.Identifier[] | undefined,
  typeCode: string
): { system: string; value: string; extension: string } => {
  const identifier = (identifiers ?? []).find(id => id.type?.coding?.some(coding => coding.code === typeCode));
  const extension = identifier?.extension?.find(ext => ext.valueString);

  return {
    system: identifier?.system || "",
    value: identifier?.value || "",
    extension: extension?.valueString || "",
  };
};

export interface HumanNameFormatOptions {
  include?: Array<"prefix" | "given" | "family" | "suffix" | "use" | "first" | "middle">;
  lineSeparator?: string;
  includeTrailingWhitespace?: boolean;
}

/**
 * Formats a FHIR HumanName as a string.
 * @param name - The name to format.
 * @param options - Optional name format options.
 * @returns The formatted name string.
 */
export function formatHumanName(name: fhirR4.HumanName, options?: HumanNameFormatOptions): string {
  const builder = [];
  const finalOrder = options?.include || ["prefix", "given", "family", "suffix"];

  for (const part of finalOrder) {
    if (!options?.include || options?.include?.includes(part)) {
      if (part === "prefix" && name?.prefix) {
        builder.push(name.prefix.join(" "));
      } else if (part === "first" && name?.given && name.given.length > 0) {
        builder.push(name.given[0]);
      } else if (part === "middle" && name?.given && name.given.length > 1) {
        builder.push(name.given.slice(1).join(" "));
      } else if (part === "given" && name?.given) {
        builder.push(name.given.join(" "));
      } else if (part === "family" && name?.family) {
        builder.push(name.family);
      } else if (part === "suffix" && name?.suffix) {
        builder.push(name.suffix.join(" "));
      }
    }
  }

  let finalName = builder.join(options?.lineSeparator ?? ", ");
  // If includeTrailingWhitespace is true, we trim leading whitespace only.
  finalName = options?.includeTrailingWhitespace === true ? trimLeadingWhitespace(finalName) : finalName.trim();

  if (name?.use && (!options?.include || options?.include?.includes("use"))) {
    finalName += " [" + name.use + "]";
  }

  return finalName;
}

export function getPractitionerPhoneNumber(practitioner: fhirR4.Practitioner): string {
  const telecom = practitioner.telecom?.find(t => t.system === "phone");
  return telecom?.value || "Unknown";
}

export function formatPractitionerName(practitioner: fhirR4.Practitioner): string {
  return (
    formatHumanName(practitioner?.name?.[0] as fhirR4.HumanName, {
      include: ["given", "family"],
      lineSeparator: " ",
    }) || `Practitioner-${practitioner.id || "unknown"}`
  );
}

export function formatRelatedPersonName(person: fhirR4.RelatedPerson): string {
  return (
    formatHumanName(person?.name?.[0] as fhirR4.HumanName, {
      include: ["given", "family"],
      lineSeparator: " ",
    }) || `RelatedPerson-${person.id || "unknown"}`
  );
}

/**
 * Sorts an array of practitioners by their formatted name.
 * @param practitioners - The array of practitioners to sort.
 * @returns A new sorted array of practitioners.
 */
export function sortPractitionersByName(practitioners: fhirR4.Practitioner[]): fhirR4.Practitioner[] {
  return [...practitioners].sort((a, b) => {
    const nameA = formatPractitionerName(a);
    const nameB = formatPractitionerName(b);
    return nameA.localeCompare(nameB);
  });
}
export interface AddressFormatOptions {
  include?: Array<"line" | "city" | "state" | "postalCode" | "country" | "use">;
  lineSeparator?: string;
}

/**
 * Utility function to format a FHIR Address as a string.
 * @param address - The address to format.
 * @param options - Optional address format options. If not provided, all address components will be included.
 * @returns The formatted address string.
 */
export function formatAddress(address: fhirR4.Address, options?: AddressFormatOptions): string {
  const builder = [];

  if (address.line && (!options?.include || options.include.includes("line"))) {
    builder.push(...address.line);
  }

  if (
    ((!options?.include || options.include.includes("city")) && address.city) ||
    ((!options?.include || options.include.includes("state")) && address.state) ||
    ((!options?.include || options.include.includes("postalCode")) && address.postalCode)
  ) {
    const cityStatePostalCode = [];
    if (address.city && (!options?.include || options.include.includes("city"))) {
      cityStatePostalCode.push(address.city);
    }
    if (address.state && (!options?.include || options.include.includes("state"))) {
      cityStatePostalCode.push(address.state);
    }
    if (address.postalCode && (!options?.include || options.include.includes("postalCode"))) {
      cityStatePostalCode.push(address.postalCode);
    }
    builder.push(cityStatePostalCode.join(", "));
  }

  if (address.country && (!options?.include || options.include.includes("country"))) {
    builder.push(address.country);
  }

  let finalAddress = builder.join(options?.lineSeparator ?? ", ").trim();

  // Append the address use without a separator if it exists
  if (builder.length > 0 && address.use && (!options?.include || options.include.includes("use"))) {
    if (address.use) {
      finalAddress += ` [${address.use}]`;
    }
  }

  return finalAddress;
}

/**
 * Formats a FHIR ContactPoint as a string.
 * @param contacts - The list of contacts to format.
 * @param contactType - The type of contact to format.
 * @param rank - The rank of the contact to format.
 * @returns The formatted contact string.
 */
export function formatContact(
  contacts: fhirR4.ContactPoint[],
  contactType: fhirR4.ContactPoint.SystemEnum,
  rank?: number
): string {
  // Return the contact of the specified type based on the provided rank or default criteria
  const contact = contacts.find(contact => contact.system === contactType && (rank ? contact.rank === rank : true));

  return contact?.value || "";
}

/**
 * Formats a FHIR Reference as a string.
 * @param value - The reference to format.
 * @returns The formatted reference string.
 */
export function formatReferenceString(value: fhirR4.Reference | undefined): string {
  if (!value) {
    return "";
  }

  return value.display ?? value.reference ?? JSON.stringify(value);
}

/**
 * Extracts the MRN from a FHIR Patient resource.
 * @param patient
 */
export const extractMRN = (patient: fhirR4.Patient) => {
  return getIdentifier(patient?.identifier, MRN_TYPE_CODE);
};

/**
 * Extracts the HCN from a FHIR Patient resource.
 * @param patient
 */
export const extractHCN = (patient: fhirR4.Patient) => {
  return getIdentifier(patient?.identifier, HCN_TYPE_CODE);
};

/**
 * Utility function to return a Date from a (string | Date) representation of a Date.
 *
 * @param dateOrDateString - The formatted date string or Date. If `dateOrDateString` is a `Date`, then it will be returned as is.
 * @returns A Date or undefined.
 */
export const convertToDate = (dateOrDateString: string | Date | undefined): Date | undefined => {
  if (!dateOrDateString) {
    return undefined;
  }

  const finalDate = (dateOrDateString as string) ? new Date(dateOrDateString as string) : (dateOrDateString as Date);
  return finalDate ?? undefined;
};

/**
 * Settings passed into functions to display the name of a patient or practitioner in a `fhirR4.Reference`.
 *
 * @param defaulDisplayName - If defined, will return this display name if no other name information is found.
 * @returns a human-readable version of the reference's display name.
 */
export type ReferenceDisplayNameSettings = {
  defaultDisplayName?: string;
};

/**
 * Utility function to return a fhirR4.Reference given a fhirR4.Patient.
 *
 * @param patient - The fhirR4.Patient to get the fhirR4.Reference for.
 * @param displayNameSettings - If defined, will include the display name in the returned `fhirR4.Reference`
																if one can be found in the `patient.name`, otherwise, will include `displayNameSettings.defaultDisplayName`.
 * @returns The patient's fhirR4.Reference. Returns `undefined` if the fhirR4.Patient does not have an id.
 */
export const getPatientReferenceFromPatient = (
  patient: fhirR4.Patient,
  displayNameSettings?: ReferenceDisplayNameSettings
): fhirR4.Reference | undefined => {
  if (!patient.id) {
    return undefined;
  }

  const humanName = patient.name?.[0];
  const displayName = displayNameSettings
    ? humanName
      ? formatHumanName(humanName, {
          include: ["given", "family"],
          lineSeparator: " ",
        })
      : displayNameSettings.defaultDisplayName
    : undefined;

  return {
    reference: `Patient/${patient.id}`,
    display: displayName,
  };
};

/**
 * Utility function to return a fhirR4.Reference for the current logged in practitioner.
 *
 * @param practitionerId - The practitioner's ID (from getIdentity().id)
 * @param practitionerName - The practitioner's display name (from getIdentity().name)
 * @param displayNameSettings - If defined, will include the display name in the returned `fhirR4.Reference`
 *                              using practitionerName or displayNameSettings.defaultDisplayName as fallback.
 * @returns The current logged in practitioner's fhirR4.Reference. Returns `undefined` if practitionerId is not provided.
 */
export const getPractitionerReference = (
  practitionerId: string | undefined,
  practitionerName?: string,
  displayNameSettings?: ReferenceDisplayNameSettings
): fhirR4.Reference | undefined => {
  if (!practitionerId) {
    return undefined;
  }

  const displayName = displayNameSettings ? (practitionerName ?? displayNameSettings.defaultDisplayName) : undefined;

  return {
    reference: formatters.formatPractitioner(practitionerId),
    display: displayName,
  };
};

/**
 * Create a chat user reference for the practitioner.
 *
 * @param practitionerId - The practitioner ID from useGetIdentity()
 * @param practitionerName - The practitioner display name from useGetIdentity()
 * @returns The chat user reference, or undefined if no ID is available
 */
export const getChatUserReference = (
  practitionerId?: string,
  practitionerName?: string
): fhirR4.Reference | undefined => {
  return getPractitionerReference(practitionerId, practitionerName);
};

// The `AlertSeverityMap` allows us to quickly find the number of active alerts or unread messages for a specific patient
// for the given alert severity.
export type AlertSeverityMap = Map<AlertSeverityFilterEnum, PatientToAlertOrUnreadMessagesCountMap>;
export type PatientToAlertOrUnreadMessagesCountMap = Map<fhirR4.Id, number>;

/**
 * Utility function that takes a list of tasks and creates an `AlertSeverityMap`.
 *
 * NOTE: The `AlertSeverityMap` allows us to quickly find the number of active alerts for a specific patient
 * for the given alert severity.
 *
 * The `AlertSeverityMap` takes keys of the form `alertSeverity` and maps them to a `PatientToAlertOrUnreadMessagesCountMap`.
 * The `PatientToAlertOrUnreadMessagesCountMap` maps patient ids to the number of active alerts for that patient based on the alert severity and state.
 *
 * @param tasks - The list of tasks used to determine the number of critical and moderate alerts in the resulting map.
 * @returns An AlertSeverityMap.
 */
export const createAlertSeverityMap = (tasks: fhirR4.Task[]): AlertSeverityMap => {
  const patientNotificationCountsMap: AlertSeverityMap = new Map();
  const addPatientNotificationCount = (
    patientId: string,
    alertSeverity: AlertSeverityFilterEnum,
    count: number
  ): void => {
    const allPatientNotificationCounts = patientNotificationCountsMap.get(alertSeverity) || new Map();
    const currentPatientNotificationCount = (allPatientNotificationCounts.get(patientId) || 0) + count;
    allPatientNotificationCounts.set(patientId, currentPatientNotificationCount);
    patientNotificationCountsMap.set(alertSeverity, allPatientNotificationCounts);

    // NOTE: When we add a count for a severity alert, we also add a count for the "all" severity.
    switch (alertSeverity) {
      case AlertSeverityFilterEnum.severe:
      case AlertSeverityFilterEnum.critical:
      case AlertSeverityFilterEnum.moderate:
      case AlertSeverityFilterEnum.routine:
        addPatientNotificationCount(patientId, AlertSeverityFilterEnum.all, 1);
    }
  };

  // Process tasks — count every task's priority equally
  tasks.forEach(task => {
    const patientId = extractFhirResourceId(task.for?.reference, "Patient");

    if (patientId && task.priority) {
      const severity = mapPriorityToSeverity(task.priority);
      addPatientNotificationCount(patientId, severity, 1);
    }
  });

  return patientNotificationCountsMap;
};

/**
 * Utility function that returns the patient ids that
 * - have a number of active alerts (if active state or asking for messages) for the given severity
 * - are resolved (if resolved state) - severity is ignored
 * NOTE: If the alertSeverity is `messages`, then the alertState is ignored.
 *
 * e.g. If the alertSeverity is `critical` and the alertState is `active`, then the function will return the patient ids that have active critical alerts.
 * e.g. If the alertState is `resolved`, then the function will return the only patient ids that have completed ALL alerts.
 * e.g. If the alertSeverity is `messages` and the alertState is `active` or `resolved`, then the function will return the patient ids that have unread messages.
 *
 * @param alertSeverityMap - The map that maps alert severity to patient notification counts. Already filtered based on active/resolved state.
 * @param alertSeverity - The alert severity to get the patient ids for (n active state). Ignored if alertState is `resolved`.
 * @param alertState - The alert state to get the patient ids for.
 * @returns An array of patient ids.
 */
export const getPatientIdsFromMap = (
  alertSeverityMap: AlertSeverityMap,
  alertSeverity: AlertSeverityFilterEnum,
  alertState: AlertStateFilterEnum
): string[] => {
  // if active or messages, just narrow down on the severity
  // if resolved, get all the patients ids since alertSeverity map has already been filtered to only include resolved patients
  const patientNotificationCountMap =
    alertState === AlertStateFilterEnum.active || alertSeverity == AlertSeverityFilterEnum.messages
      ? alertSeverityMap.get(alertSeverity)
      : alertSeverityMap.get(AlertSeverityFilterEnum.all);
  return patientNotificationCountMap ? Array.from(patientNotificationCountMap.keys()) : [];
};

/**
 * Utility function that returns the notification count (i.e. active critical alerts, active moderate alerts & unread messages)
 * for a patient or for all patients.
 *
 * @param alertSeverityAndStateMap - The map that maps alert severity and state selection to patient notification counts.
 * @param patientId - The id of the patient to get the notification count for. If not provided, the notification count for all patients is returned.
 * @returns A tuple with the following structure: { critical: {number of critical active alerts}, moderate: {number of moderate active alerts}, messages: { number of unread messages }}.
 */
export const getPatientNotificationCountFromMap = (
  alertSeverityAndStateMap: AlertSeverityMap,
  patientId: string | undefined = undefined
): { all: number; severe: number; critical: number; moderate: number; routine: number; messages: number } => {
  const allCount = getPatientNotificationCountForSeverityFromMap(
    alertSeverityAndStateMap,
    AlertSeverityFilterEnum.all,
    patientId
  );
  const severeCount = getPatientNotificationCountForSeverityFromMap(
    alertSeverityAndStateMap,
    AlertSeverityFilterEnum.severe,
    patientId
  );
  const criticalCount = getPatientNotificationCountForSeverityFromMap(
    alertSeverityAndStateMap,
    AlertSeverityFilterEnum.critical,
    patientId
  );
  const moderateCount = getPatientNotificationCountForSeverityFromMap(
    alertSeverityAndStateMap,
    AlertSeverityFilterEnum.moderate,
    patientId
  );
  const routineCount = getPatientNotificationCountForSeverityFromMap(
    alertSeverityAndStateMap,
    AlertSeverityFilterEnum.routine,
    patientId
  );
  const messagesCount = getPatientNotificationCountForSeverityFromMap(
    alertSeverityAndStateMap,
    AlertSeverityFilterEnum.messages,
    patientId
  );

  return {
    all: allCount,
    severe: severeCount,
    critical: criticalCount,
    moderate: moderateCount,
    routine: routineCount,
    messages: messagesCount,
  };
};

/**
 * Utility function that returns the notification count for a specific severity for a patient or for all patients.
 * 1. For `AlertSeverityFilterEnum.all` the total number of critical active and moderate active alerts is returned.
 * 2. For `AlertSeverityFilterEnum.critical` the number of critical active alerts is returned.
 * 3. For `AlertSeverityFilterEnum.moderate` the number of moderate active alerts is returned.
 * 4. For `AlertSeverityFilterEnum.messages` the number of unread messages is returned.
 *
 * @param alertSeverityAndStateMap - The map that maps alert severity and state selection to patient notification counts.
 * @param alertSeverity - The alert severity to get the notification count for.
 * @param patientId - The id of the patient to get the notification count for. If not provided, the notification count for all patients is returned.
 * @returns A tuple with the following structure: { critical: {number of critical active alerts}, moderate: {number of moderate active alerts}, messages: { number of unread messages }}.
 */
export const getPatientNotificationCountForSeverityFromMap = (
  alertSeverityAndStateMap: AlertSeverityMap,
  alertSeverity: AlertSeverityFilterEnum,
  patientId: string | undefined = undefined
): number => {
  if (!patientId) {
    return Array.from(alertSeverityAndStateMap.get(alertSeverity)?.values() ?? []).reduce((a, b) => a + b, 0);
  } else {
    return alertSeverityAndStateMap.get(alertSeverity)?.get(patientId) || 0;
  }
};

/**
 * Utility function that returns a UUID `fhirR4.Resource` reference that can be used in `fhirR4.Bundles` to allow one `fhirR4.Resource` to reference another.
 *
 * @returns A uuid reference.
 * @remarks The reference is generated using `uuidv4()`.
 */
export const createUUIDResourceReference = (): string => {
  return "urn:uuid:" + uuidv4();
};

interface CreateResourceBundleEntryForUploadProps {
  resource: fhirR4.Resource;
  httpMethod: HttpMethod;
}

/**
 * Utility function that returns a `fhirR4.BundleEntry` for the provided resource.
 *
 * @param resource - The `fhirR4.Resource` to return a `fhirR4.BundleEntry` for.
 * @returns A `fhirR4.BundleEntry` for the provided resource with a `fullUrl` and a `request` object with a `POST` method and `url`.
 * @remarks The `fullUrl` is generated using `uuidv4()`.
 */
export const createResourceBundleEntryForUpload = ({
  resource,
  httpMethod,
}: CreateResourceBundleEntryForUploadProps): fhirR4.BundleEntry => {
  const fullUrl = resource.id ?? createUUIDResourceReference();

  return {
    fullUrl: fullUrl,
    resource: resource,
    request: {
      method: httpMethod,
      url: resource.resourceType,
    },
  };
};

/**
 * Creates a DELETE `fhirR4.BundleEntry` for the given resource type and ID.
 * DELETE entries have no `resource` body — only a `request`.
 */
export const createDeleteBundleEntry = (resourceType: string, id: string): fhirR4.BundleEntry => {
  const url = `${resourceType}/${id}`;
  return {
    request: {
      method: HttpMethod.DELETE,
      url,
    },
  };
};

interface CreateResourceBundleEntryForBinaryPatchProps {
  requestUrl: string;
  resource: fhirR4.Binary;
}

/**
 * Utility function that returns a `fhirR4.BundleEntry` for the provided resource.
 *
 * @param requestUrl - The url to use for the `fullUrl` and `url` in the request object.
 * @param resourceName - The name of the resource to return a `fhirR4.BundleEntry` for.
 * 											 Since a Patch request could use a different resource name than the one in the resource
 * 											 (e.g. passing in a `Binary` resource), this is passed in as a parameter.
 * @param resource - The `fhirR4.Resource` to return a `fhirR4.BundleEntry` for.
 * @returns A `fhirR4.BundleEntry` for the provided resource with a `fullUrl` and a `request` object with a `PATCH` method and `url`.
 */
export const createResourceBundleEntryForPatchBinary = ({
  requestUrl,
  resource,
}: CreateResourceBundleEntryForBinaryPatchProps): fhirR4.BundleEntry => {
  return {
    fullUrl: requestUrl,
    resource: resource,
    request: {
      method: "PATCH",
      url: requestUrl,
    },
  };
};

interface CreateResourceBundleEntryForParametersPatchProps {
  requestUrl: string;
  resource: fhirR4.Parameters;
}

/**
 * Utility function that returns a `fhirR4.BundleEntry` for the provided resource.
 *
 * @param requestUrl - The url to use for the `fullUrl` and `url` in the request object.
 * @param resource - The `fhirR4.Resource` to return a `fhirR4.BundleEntry` for.
 * @returns A `fhirR4.BundleEntry` for the provided resource with a `fullUrl` and a `request` object with a `PATCH` method and `url`.
 */
export const createResourceBundleEntryForPatchParameters = ({
  requestUrl,
  resource,
}: CreateResourceBundleEntryForParametersPatchProps): fhirR4.BundleEntry => {
  return {
    fullUrl: requestUrl,
    resource: resource as unknown as fhirR4.Resource,
    request: {
      method: "PATCH",
      url: requestUrl,
    },
  };
};

interface CreateResourceBundleEntryForJsonOperationsPatchProps {
  requestUrl: string;
  resource: JsonPatchResource;
}

/**
 * Utility function that returns a `fhirR4.BundleEntry` for the provided resource.
 *
 * @param requestUrl - The url to use for the `fullUrl` and `url` in the request object.
 * @param resource - The `JsonPatchResource` to return a `fhirR4.BundleEntry` for.
 * @returns A `fhirR4.BundleEntry` for the provided resource with a `fullUrl` and a `request` object with a `PATCH` method and `url`.
 */
export const createResourceBundleEntryForPatchJsonDocument = ({
  requestUrl,
  resource,
}: CreateResourceBundleEntryForJsonOperationsPatchProps): fhirR4.BundleEntry => {
  return {
    fullUrl: requestUrl,
    resource: resource as unknown as fhirR4.Resource,
    request: {
      method: HttpMethod.PATCH,
      url: requestUrl,
    },
  };
};

/**
 * Utility function that returns the fhir endpoint we need to target for `useCreate` given the `fhirBaseUrl` and `resourceType`.
 *
 * @param fhirBaseUrl - The base url of the FHIR server (e.g. `https://yourserver.com/fhir`).
 * @param resourceType - The type of the resource for which to create the endpoint.
 * @returns The url to use.
 * @remarks This function was needed because `fhirR4.Bundle` is unique in that it needs to be `POST`ed to `http://yourserver.com/fhir` instead of
 * 					`http://yourserver.com/fhir/{resourceType}`.
 */
export const endpointForUseCreate = (fhirBaseUrl: string, resourceType: string): string => {
  if (resourceType === FhirResourceType.Bundle) {
    return fhirBaseUrl;
  }

  return `${fhirBaseUrl}/${resourceType}`;
};

/**
 * Utility function that returns a `fhirR4.CodeableConcept` for the provided mime type.
 *
 * @param mimeType - The mime type of the `fhirR4.Media` for which to create the `fhirR4.CodeableConcept`.
 * @returns A `fhirR4.CodeableConcept` that classifies the media type as `image`, `audio` or `video`.
 * 					Returns `undefined` if the mime type is not one of the other types.
 */
export const createMediaTypeCodeableConceptFromMimeType = (mimeType: string): fhirR4.CodeableConcept | undefined => {
  if (mimeType.startsWith(`${FhirR4MediaTypeCode.IMAGE}/`)) {
    return createMediaTypeCodeableConceptFromMediaType(FhirR4MediaTypeCode.IMAGE);
  } else if (mimeType.startsWith(`${FhirR4MediaTypeCode.VIDEO}/`)) {
    return createMediaTypeCodeableConceptFromMediaType(FhirR4MediaTypeCode.VIDEO);
  } else if (mimeType.startsWith(`${FhirR4MediaTypeCode.AUDIO}/`)) {
    return createMediaTypeCodeableConceptFromMediaType(FhirR4MediaTypeCode.AUDIO);
  } else {
    return undefined;
  }
};

/**
 * Utility function that returns a `fhirR4.CodeableConcept` for the provided `FhirR4MediaTypeCode`.
 *
 * @param mediaType - The media type of the `fhirR4.Media` for which to create the `fhirR4.CodeableConcept`.
 * @returns A `fhirR4.CodeableConcept` that classifies the media type as `image`, `audio` or `video`.
 */
export const createMediaTypeCodeableConceptFromMediaType = (
  mediaType: FhirR4MediaTypeCode
): fhirR4.CodeableConcept | undefined => {
  const imageConcept: fhirR4.CodeableConcept = {
    coding: [
      {
        system: FHIR_MEDIA_TYPE_SYSTEM,
        code: FhirR4MediaTypeCode.IMAGE,
        display: FhirR4MediaTypeCode.IMAGE,
      },
    ],
  };

  const videoConcept: fhirR4.CodeableConcept = {
    coding: [
      {
        system: FHIR_MEDIA_TYPE_SYSTEM,
        code: FhirR4MediaTypeCode.VIDEO,
        display: FhirR4MediaTypeCode.VIDEO,
      },
    ],
  };

  const audioConcept: fhirR4.CodeableConcept = {
    coding: [
      {
        system: FHIR_MEDIA_TYPE_SYSTEM,
        code: FhirR4MediaTypeCode.AUDIO,
        display: FhirR4MediaTypeCode.AUDIO,
      },
    ],
  };

  switch (mediaType) {
    case FhirR4MediaTypeCode.IMAGE:
      return imageConcept;
    case FhirR4MediaTypeCode.AUDIO:
      return audioConcept;
    case FhirR4MediaTypeCode.VIDEO:
      return videoConcept;
  }
};

/**
 * Returns the `accountInviteDate` value found in the `extension` of the `Patient` or `RelatedPerson`.
 * @param user - The FHIR Patient or RelatedPerson.
 * @returns The resulting `accountInviteDate` value or undefined if it doesn't exist.
 */
export const getAccountInviteDate = (user: fhirR4.RelatedPerson | fhirR4.Patient): Date | undefined => {
  const inviteDateString = user.extension?.find(ext => ext.url === USER_INVITE_DATE_EXTENSION_URL)?.valueDateTime;
  return inviteDateString ? convertToDate(inviteDateString) : undefined;
};

/**
 * Returns a copy of the `Extension[]` in `extension` of `Patient` or `RelatedPerson` and updates or adds the `accountInviteDate` value in it.
 * @param user - The FHIR RelatedPerson resource.
 * @param accountInviteDate - The date the last account invite was sent. If undefined, the value will be removed.
 * @returns The resulting `Extension[]` array with the value `accountInviteDate` saved.
 * @remarks This function does not modify the input `user` object, it returns a new `Extension[]`.
 */
export const getUpdatedExtensionSettingAccountInviteDate = (
  user: fhirR4.Patient | fhirR4.RelatedPerson,
  accountInviteDate: Date | undefined
) => {
  const updatedExtensions = user.extension != undefined ? user.extension : [];
  const existingExtensionIndex = updatedExtensions.findIndex(ext => ext.url === USER_INVITE_DATE_EXTENSION_URL);

  // If the extension already exists, we update it or remove it if accountInviteDate is undefined.
  if (existingExtensionIndex !== -1) {
    if (accountInviteDate) {
      // Update the existing extension
      updatedExtensions[existingExtensionIndex] = {
        ...updatedExtensions[existingExtensionIndex],
        valueDateTime: accountInviteDate.toISOString(),
      };
    } else {
      // Remove the extension if accountInviteDate is undefined
      updatedExtensions.splice(existingExtensionIndex, 1);
    }
  } else {
    if (accountInviteDate) {
      updatedExtensions.push({
        url: USER_INVITE_DATE_EXTENSION_URL,
        valueDateTime: accountInviteDate.toISOString(),
      });
    }
  }

  // We return undefined if there are no extensions left.
  return updatedExtensions.length > 0 ? updatedExtensions : undefined;
};

/**
 * Returns a `fhirR4.Bundle` for the provided resources.
 * @param resources - The FHIR resources to include in the bundle.
 * @returns A `fhirR4.Bundle` containing the provided resources as entries.
 */
export const createFhirBundleForResources = (resources: fhirR4.Resource[]): fhirR4.Bundle => {
  const httpMethod = HttpMethod.POST;
  const bundleEntries = resources.map(resource => createResourceBundleEntryForUpload({ resource, httpMethod }));

  const bundle: fhirR4.Bundle = {
    resourceType: FhirResourceType.Bundle,
    type: "transaction",
    entry: bundleEntries,
  };

  return bundle;
};

/**
 * Returns a `fhirR4.Identifier[]` for `fhirR4.Patient` with any invalid identifiers removed.
 * @param patient - The patient to remove any invalid identifiers from.
 * @returns A `fhirR4.Indentifier[]` or undefined if no valid identifiers exist.
 * @remarks An identifier is considered invalid if its value is undefined, null or an empty string.
 * @remarks This function also removes extensions with empty/undefined valueString values.
 * @remarks This function doesn't modify the input `patient` object, it returns a new `fhirR4.Identifier[]` or undefined.
 */
export const getPatientIdentifiersRemovingInvalidValues = (
  patient: fhirR4.Patient
): fhirR4.Identifier[] | undefined => {
  const newIdentifiers = (patient.identifier || [])
    .filter(identifier => identifier.value && identifier.value.trim() !== "")
    .map(identifier => {
      // Clean up extensions: remove those without valueString
      if (identifier.extension && identifier.extension.length > 0) {
        const validExtensions = identifier.extension.filter(ext => ext.valueString && ext.valueString.trim() !== "");
        // Return identifier without extension field if no valid extensions remain
        if (validExtensions.length === 0) {
          const { extension: _extension, ...identifierWithoutExtension } = identifier;
          return identifierWithoutExtension;
        }
        return { ...identifier, extension: validExtensions };
      }
      return identifier;
    });
  return newIdentifiers.length > 0 ? newIdentifiers : undefined;
};

/**
 * FHIR R4 resource types (matches backend pattern).
 */
export enum FhirResourceType {
  Patient = "Patient",
  Practitioner = "Practitioner",
  PractitionerRole = "PractitionerRole",
  Organization = "Organization",
  RelatedPerson = "RelatedPerson",
  CareTeam = "CareTeam",
  Task = "Task",

  Media = "Media",
  Bundle = "Bundle",
  Binary = "Binary",
  Parameters = "Parameters",
  Observation = "Observation",
  QuestionnaireResponse = "QuestionnaireResponse",
  Medication = "Medication",
  MedicationRequest = "MedicationRequest",
  MedicationStatement = "MedicationStatement",
  CarePlan = "CarePlan",
  Goal = "Goal",
  AllergyIntolerance = "AllergyIntolerance",
  Condition = "Condition",
  DiagnosticReport = "DiagnosticReport",
  ClinicalImpression = "ClinicalImpression",
  CommunicationRequest = "CommunicationRequest",
  AuditEvent = "AuditEvent",
}

/**
 * Formats a FHIR resource reference (e.g., "Patient/123").
 */
const formatFhirResource = (resourceType: FhirResourceType, id: string): string => {
  return `${resourceType}/${id}`;
};

/**
 * Dynamic formatters for FHIR resource references.
 * Usage: formatters.formatOrganization("123") -> "Organization/123"
 */
const fhirResources = Object.values(FhirResourceType);
export const formatters = Object.fromEntries(
  fhirResources.map(_r => [`format${_r}`, (id: string) => formatFhirResource(_r, id)])
) as Record<`format${FhirResourceType}`, (id: string) => string>;

// ============================================================
// Array Utilities for FHIR Resources
// ============================================================

/**
 * Splits an array into chunks of specified size.
 * Used to avoid 403 errors when querying with too many comma-separated values.
 * @param arr - Array to chunk
 * @param size - Maximum chunk size
 * @returns Array of chunks
 */
export const chunk = <T>(arr: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
};

/**
 * Deduplicates FHIR resources by ID.
 * @param items - Array of FHIR resources with optional id field
 * @returns Deduplicated array (last occurrence wins)
 */
export const deduplicateById = <T extends { id?: string }>(items: T[]): T[] => {
  const byId = new Map<string, T>();
  items.forEach(item => {
    if (item.id) {
      byId.set(item.id, item);
    }
  });
  return Array.from(byId.values());
};
